import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";
import Select from "../../components/ReportSelect";
import { saveAs } from "file-saver";
import { Container, Table, Button, Row, Col, Spinner } from "react-bootstrap";
import Pagination from "../../components/Pagination";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import { filterReportRows } from "../../utils/reportSearch";
import "../../styles/Reports.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

// Default date range
const today = dayjs().format("YYYY-MM-DD");
const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "");
const toISO = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "");

export default function PositionHistoryReport() {
  const tableRef = useRef(null);
  const navigate = useNavigate();

  // Dropdown sources
  const [orgUnits, setOrgUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCats, setSubCats] = useState([]);

  // Table rows
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Page-level filter UI inputs
  const [pStartDate, setPStartDate] = useState(oneMonthAgo);
  const [pEndDate, setPEndDate] = useState(today);
  const [pOrgUnit, setPOrgUnit] = useState(null);
  const [pCategory, setPCategory] = useState(null);
  const [pSubCat, setPSubCat] = useState(null);
  const [pSearch, setPSearch] = useState("");

  // Actual applied filters
  const [fStartDate, setFStartDate] = useState("");
  const [fEndDate, setFEndDate] = useState("");
  const [fOrgUnit, setFOrgUnit] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fSubCat, setFSubCat] = useState("");

  // Load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [ou, cat, sub] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
        ]);

        setOrgUnits(ou.data || []);
        setCategories(cat.data || []);
        setSubCats(sub.data || []);
      } catch (err) {
        console.error("Dropdown load error:", err);
      }
    };

    loadDropdowns();
  }, []);

  // Fetch rows
  const fetchRows = useCallback(async () => {
    setLoading(true);
    setCurrentPage(1);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const res = await axios.get(`${BASE_URL}/api/reports/position-history`, {
        headers,
        params: {
          startDate: fStartDate,
          endDate: fEndDate,
          orgUnit: fOrgUnit,
          category: fCategory,
          subCategory: fSubCat,
        },
      });

      setRows(res.data || []);
    } catch (err) {
      console.error("Position History fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fStartDate, fEndDate, fOrgUnit, fCategory, fSubCat]);

  useEffect(() => {
    if (!hasSearched) return;
    fetchRows();
  }, [hasSearched, fetchRows]);

  // Apply filters
  const applyFilters = () => {
    setHasSearched(true);
    setFStartDate(pStartDate);
    setFEndDate(pEndDate);
    setFOrgUnit(pOrgUnit?.value || "");
    setFCategory(pCategory?.value || "");
    setFSubCat(pSubCat?.value || "");
  };

  // Clear filters
  const clearFilters = () => {
    setPStartDate(oneMonthAgo);
    setPEndDate(today);
    setPOrgUnit(null);
    setPCategory(null);
    setPSubCat(null);
    setPSearch("");

    setFStartDate("");
    setFEndDate("");
    setFOrgUnit("");
    setFCategory("");
    setFSubCat("");

    setRows([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  // Pagination
  const searchedRows = filterReportRows(rows, pSearch);
  const sortConfig = useClientTableSort(tableRef, () => setCurrentPage(1));
  const sortedRows = sortRowsByConfig(searchedRows, sortConfig, [
    (row) => row.org_unit,
    (row) => row.first_name,
    (row) => row.last_name,
    (row) => row.emp_category,
    (row) => row.emp_sub_category,
    (row) => row.position,
    (row) => row.ph_start_date,
    (row) => row.ph_end_date,
    (row) => row.ph_reason_for_change,
  ]);
  const paginated = sortedRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Export CSV
  const exportCSV = () => {
    if (!hasSearched || searchedRows.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Org Unit",
      "First Name",
      "Last Name",
      "Emp Category",
      "Sub Category",
      "Position",
      "Start Date",
      "End Date",
      "Reason for Change",
    ];

    const lines = searchedRows.map((r) => {
      const cols = [
        r.org_unit,
        r.first_name,
        r.last_name,
        r.emp_category,
        r.emp_sub_category,
        r.position,
        fmt(r.ph_start_date),
        fmt(r.ph_end_date),
        r.ph_reason_for_change,
      ];

      return cols
        .map((c) => `"${String(c || "").replace(/"/g, '""')}"`)
        .join(",");
    });

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, "PositionHistoryReport.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-briefcase" /></span><span>Position History</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filter Row */}
      <ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            value={pStartDate}
            onChange={(e) => setPStartDate(toISO(e.target.value))}
          />
        </Col>

        <Col>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            value={pEndDate}
            onChange={(e) => setPEndDate(toISO(e.target.value))}
          />
        </Col>

        <Col>
          <label>Org Unit</label>
          <Select
            options={orgUnits.map((o) => ({
              value: o.ou_id,
              label: o.ou_name,
            }))}
            value={pOrgUnit}
            onChange={setPOrgUnit}
          />
        </Col>

        <Col>
          <label>Category</label>
          <Select
            options={categories.map((c) => ({
              value: c.c_id,
              label: c.c_name,
            }))}
            value={pCategory}
            onChange={setPCategory}
          />
        </Col>

        <Col>
          <label>Sub Category</label>
          <Select
            options={subCats.map((sc) => ({
              value: sc.sc_id,
              label: sc.sc_name,
            }))}
            value={pSubCat}
            onChange={setPSubCat}
          />
        </Col>
      </Row>

      {/* Buttons */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search</label>
          <input type="text" className="form-control" placeholder="ID / Name / Keyword" value={pSearch} onChange={(event) => setPSearch(event.target.value)} />
        </Col>
      </Row>
      </ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto">
          <div className="button-group">
            <Button variant="primary" onClick={applyFilters}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>

        <Col md="auto" className="ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || searchedRows.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table
            ref={tableRef}
            data-nw-client-sort="true"
            bordered
            hover
            size="sm"
            className="compact-table"
          >
            <thead className="table-dark text-center">
              <tr>
                <th>Org Unit</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Emp Category</th>
                <th>Sub Category</th>
                <th>Position</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Reason</th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paginated.map((r, i) => (
                  <tr key={i}>
                    <td className="text-center">{r.org_unit}</td>
                    <td className="text-center">{r.first_name}</td>
                    <td className="text-center">{r.last_name}</td>
                    <td className="text-center">{r.emp_category}</td>
                    <td className="text-center">{r.emp_sub_category}</td>
                    <td className="text-center">{r.position}</td>
                    <td className="text-center">{fmt(r.ph_start_date)}</td>
                    <td className="text-center">{fmt(r.ph_end_date)}</td>
                    <td className="text-center">{r.ph_reason_for_change}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={sortedRows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
